import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

// Actions - based on existing patterns from HistoryList_V2 and PendingList_V2
import {
  SG_GET_DEBRIEF_SCHEDULES,
  ADD_ACTIVE_PAGE_DEBRIEF_SCHEDULE,
  SG_DELETE_DEBRIEF_SCHEDULE,
  SG_GET_CULTURE_AUDIT_REPORTS,
  SG_GET_SURVEY_QUESTIONS,
  SG_GET_SURVEY_STRUCTURES,
} from 'constants/actions';

import { SURVEY_TYPE, SURVEY_INFO, SURVEY_QUESTION_TYPE } from 'constants/surveys';

// Create the Schedule Context
const ScheduleContext = createContext();

// Custom hook to use the schedule context
export const useScheduleContext = () => {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error('useScheduleContext must be used within a ScheduleProvider');
  }
  return context;
};

// Schedule Provider Component
export const ScheduleProvider = ({ children }) => {
  const dispatch = useDispatch();

  // Local state management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [fetchAgain, setFetchAgain] = useState(false);
  const [deletedRecord, setDeletedRecord] = useState(false);

  // Organization and user data
  const [organizationId, setOrganizationId] = useState(null);
  const [userAdmin, setUserAdmin] = useState(false);
  const [userEmployee, setUserEmployee] = useState(null);
  const [userEmployeeCategoryList] = useState([]);

  // Survey structure data
  const [primaryCategory, setPrimaryCategory] = useState([]);
  const [secondaryCategory, setSecondaryCategory] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [primarySecondaryName, setPrimarySecondaryName] = useState({
    primary: 'Type',
    secondary: 'Role',
  });

  // Pagination settings
  const defaultPageSize = 12;

  // Redux selectors - following the same pattern as existing files
  const {
    get_auth,
    get_organizations,
    get_employee,
    get_survey_structure,
    get_survey_questions,
    get_debrief_schedules,
    get_culture_audit_reports,
  } = useSelector(
    (state) => ({
      get_auth: state.auth,
      get_organizations: state.organizations,
      get_employee: state.employees,
      get_survey_structure: state.surveystructure?.survey_structure,
      get_survey_questions: state.surveyquestions?.survey_questions,
      get_debrief_schedules: state.debrief_schedule,
      get_culture_audit_reports: state.debrief_schedule?.culture_audit_reports,
    }),
    shallowEqual
  );

  const ambassadorId = useSelector(
    (state) => Number(state.auth?.ambassador_id),
    shallowEqual
  );

  // Initialize organization ID
  useEffect(() => {
    if (get_auth?.organization_id) {
      setOrganizationId(Number(get_auth.organization_id));
    }
  }, [get_auth]);

  // Initialize user data
  useEffect(() => {
    setUserAdmin(false);
    if (get_employee) {
      setUserEmployee(get_employee?.userEmp);
      if (get_employee.userEmp?.account_type === 5) {
        setUserAdmin(true);
      }
    }
  }, [get_employee, get_auth]);

  // Initialize survey structures and categories
  useEffect(() => {
    if (organizationId) {
      dispatch({
        type: SG_GET_SURVEY_QUESTIONS,
        payload: `survey_type=${SURVEY_QUESTION_TYPE.AUDIT}&status=2`,
      });
      dispatch({
        type: SG_GET_SURVEY_STRUCTURES,
        payload: `organization=${organizationId}`,
      });
    }
  }, [dispatch, organizationId]);

  // Set primary/secondary names from survey structure
  useEffect(() => {
    if (get_survey_structure?.[0]?.categories) {
      setPrimarySecondaryName({
        secondary: get_survey_structure[0].categories.find(
          (f) => f.priority === 'secondary'
        )?.name,
        primary: get_survey_structure[0].categories.find(
          (f) => f.priority === 'primary'
        )?.name,
      });
    }
  }, [get_survey_structure]);

  // Build category lists from survey structure
  useEffect(() => {
    const _primaryList = [];
    const _secondaryList = [];
    const _groupList = [];

    if (get_survey_structure?.[0]?.categories?.length > 0) {
      get_survey_structure[0].categories.forEach((st) => {
        if (st?.priority === 'primary') {
          st.options.forEach((str) => {
            if (str?.subfactors?.length > 0) {
              _groupList.push(str?.name);
              str.subfactors.forEach((opf) => {
                if (userAdmin || userEmployeeCategoryList?.includes(opf.name)) {
                  _primaryList.push({
                    value: opf.name,
                    name: opf?.name,
                    name_id: opf?.id,
                    label: opf?.name,
                    group: str?.name,
                    mainGroup: st?.name,
                    type_of: st?.priority,
                    subtitle: st?.subtitle,
                    priority: st?.priority,
                  });
                }
              });
            } else {
              if (userAdmin || userEmployeeCategoryList?.includes(str.name)) {
                _primaryList.push({
                  value: str?.name,
                  label: str?.name,
                  name: str?.name,
                  name_id: str?.id,
                  type_of: st?.priority,
                  subtitle: st?.subtitle,
                  priority: st?.priority,
                });
              }
            }
          });
        }

        if (st?.priority === 'secondary') {
          st.options.forEach((str) => {
            if (userAdmin || userEmployeeCategoryList?.includes(str.name)) {
              _secondaryList.push({
                value: str?.name,
                label: str?.name,
                name: str?.name,
                name_id: str?.id,
                type_of: st?.priority,
                subtitle: st?.subtitle,
                priority: st?.priority,
              });
            }
          });
        }
      });

      setPrimaryCategory(_primaryList);
      setSecondaryCategory(_secondaryList);
      setGroupList(_groupList);
    }
  }, [get_survey_structure, userAdmin, userEmployeeCategoryList]);

  // API Functions for Culture Audit Surveys (Non-Pulse)
  const fetchCultureAuditSchedules = useCallback((status = 'pending', page = 1) => {
    if (!organizationId) return;
    
    setLoading(true);
    const payload = `page=${page}&type_of=${SURVEY_TYPE.AUDIT}&organization=${organizationId}&curr-status=${status}${
      selectedPrimary?.value ? `&name=${selectedPrimary?.value?.trim()}` : ''
    }`;

    dispatch({
      type: SG_GET_DEBRIEF_SCHEDULES,
      payload,
    });
  }, [dispatch, organizationId, selectedPrimary]);

  // Fetch historical/completed surveys
  const fetchHistoricalSurveys = useCallback((page = 1) => {
    fetchCultureAuditSchedules('history', page);
  }, [fetchCultureAuditSchedules]);

  // Fetch active/pending surveys
  const fetchActiveSurveys = useCallback((page = 1) => {
    fetchCultureAuditSchedules('pending', page);
  }, [fetchCultureAuditSchedules]);

  // Fetch culture audit reports
  const fetchCultureAuditReports = useCallback(() => {
    if (!organizationId || get_culture_audit_reports?.response) return;

    const payload = ambassadorId
      ? `audit-type-id=${SURVEY_TYPE.AUDIT}&organization=${organizationId}`
      : `audit-type-id=${SURVEY_TYPE.AUDIT}`;

    dispatch({
      type: SG_GET_CULTURE_AUDIT_REPORTS,
      payload,
    });
  }, [dispatch, organizationId, ambassadorId, get_culture_audit_reports]);

  // Delete survey schedule
  const deleteSurveySchedule = useCallback((scheduleId) => {
    if (!scheduleId) return;

    dispatch({
      type: SG_DELETE_DEBRIEF_SCHEDULE,
      payload: { id: scheduleId },
    });
    setDeletedRecord(scheduleId);
    setFetchAgain(true);
  }, [dispatch]);

  // Handle pagination
  const changePage = useCallback((newPage) => {
    setActivePage(newPage);
    dispatch({
      type: ADD_ACTIVE_PAGE_DEBRIEF_SCHEDULE,
      payload: newPage,
    });
  }, [dispatch]);

  // Get total pages
  const getTotalPages = useCallback(() => {
    return Math.ceil((get_debrief_schedules?.debrief_schedule?.count || 0) / defaultPageSize);
  }, [get_debrief_schedules]);

  // Get current surveys data
  const getCurrentSurveys = useCallback(() => {
    return get_debrief_schedules?.debrief_schedule?.results || [];
  }, [get_debrief_schedules]);

  // Format survey data for consistent display
  const formatSurveyData = useCallback((surveys) => {
    return surveys.map((survey) => ({
      id: survey.id,
      title: survey.name,
      type: 'Culture Audit',
      status: survey.status_text?.toLowerCase() || 'pending',
      startDate: survey.scheduled_for,
      endDate: survey.close_at,
      participants: survey.total_requests || 0,
      responses: survey.total_responses || 0,
      completionRate: survey.total_requests > 0 
        ? `${Math.round((survey.total_responses / survey.total_requests) * 100)}%` 
        : '0%',
      statusText: survey.status_text,
      rawData: survey,
    }));
  }, []);

  // Clear errors and reset states
  const clearErrors = useCallback(() => {
    setError(null);
    setFetchAgain(false);
    setDeletedRecord(false);
  }, []);

  // Refresh data
  const refreshData = useCallback(() => {
    setFetchAgain(true);
    if (organizationId) {
      fetchActiveSurveys(activePage);
      fetchCultureAuditReports();
    }
  }, [organizationId, activePage, fetchActiveSurveys, fetchCultureAuditReports]);

  // Auto-fetch data when dependencies change
  useEffect(() => {
    if (organizationId && activePage) {
      fetchActiveSurveys(activePage);
    }
  }, [organizationId, activePage, fetchAgain, deletedRecord, selectedPrimary, fetchActiveSurveys]);

  useEffect(() => {
    fetchCultureAuditReports();
  }, [fetchCultureAuditReports]);

  // Handle loading states
  useEffect(() => {
    if (fetchAgain || deletedRecord) {
      setLoading(true);
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [fetchAgain, deletedRecord]);

  // Context value
  const value = {
    // State
    loading,
    error,
    activePage,
    organizationId,
    userAdmin,
    userEmployee,
    fetchAgain,
    deletedRecord,
    
    // Survey structure data
    primaryCategory,
    secondaryCategory,
    groupList,
    primarySecondaryName,
    selectedPrimary,
    setSelectedPrimary,
    
    // Redux data
    get_survey_questions,
    get_survey_structure,
    get_organizations,
    get_debrief_schedules,
    get_culture_audit_reports,
    
    // API functions
    fetchActiveSurveys,
    fetchHistoricalSurveys,
    fetchCultureAuditReports,
    deleteSurveySchedule,
    changePage,
    refreshData,
    clearErrors,
    
    // Data helpers
    getCurrentSurveys,
    formatSurveyData,
    getTotalPages,
    
    // Pagination
    defaultPageSize,
    
    // Survey types
    SURVEY_TYPE,
    SURVEY_INFO,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};

export default ScheduleContext;